import { StepProps } from 'components/Wizards/MultiStepWizard/Types'
import { Form } from 'components/Wizards/Form/Form'
import Styles from './BackgroundRemoval.module.scss'
import { MediaVariant } from 'types'
import { useCreateMediaVariantSync } from 'hooks/queries/mediaFiles/useCreateMediaVariant'
import { SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react'
import ToggleButton from 'common/ToggleButton/ToggleButton'
import { Text } from 'blaze-ui/Text/Text'
import { Formik, useFormikContext } from 'formik'
import { LoadingShimmerer } from 'components/LoadingShimmerer/LoadingShimmerer'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import cx from 'classnames'
import { multiStep } from 'components/Wizards/MultiStepWizard/multiStep'
import { useWizardSchema } from 'components/Wizards/Wizard/WizardContext'
import { MultiStepSchema } from 'components/Wizards/schemas/Types'
import { SegmentedControl } from 'blaze-ui/SegmentedControl/SegmentedControl'
import {
  ASPECT_RATIOS,
  AspectRatioType,
  DesignChannel,
  getAspectRatioKeyFromDesignChannel,
} from 'components/NewFileModal/Types'
import { capitalize } from 'lodash'
import { Item } from '@react-stately/collections'
import { Heading } from 'blaze-ui/Heading/Heading'
import { LoadingEllipses } from 'components/Wizards/Wizard/Form/SectionHeader'
import { Background } from 'blaze-ui/icons/24'

interface Props extends StepProps {
  initialRemoveBackground: boolean
}

export const getInitialAspectRatio = (width?: number, height?: number): AspectRatioType => {
  if (!width || !height) {
    return 'portrait'
  }

  if (width > height) {
    return 'landscape'
  } else if (width < height) {
    return 'portrait'
  } else {
    return 'square'
  }
}

export const BackgroundRemoval = multiStep<Props>((props) => {
  const { schema, onSubmit } = props

  return (
    <Formik
      validationSchema={schema}
      onSubmit={onSubmit}
      initialValues={schema.cast({ ...schema.getDefault(), ...props.initialValues }, { assert: false })}
    >
      <Content {...props} />
    </Formik>
  )
})

const Content = multiStep<Props>(({ schema, onBack, initialValues, initialRemoveBackground }) => {
  const [isBackgroundRemoved, setIsBackgroundRemoved] = useState(
    initialValues.initial_media_file_remove_background === undefined
      ? initialRemoveBackground
      : !!initialValues.initial_media_file_remove_background,
  )
  const { setFieldValue } = useFormikContext()
  const { mutate, isLoading } = useCreateMediaVariantSync()
  const abortController = useRef(new AbortController())
  const wizardSchema = useWizardSchema() as MultiStepSchema
  const originalImageRef = useRef<HTMLImageElement>(null)
  const [cutoutUrl, setCutoutUrl] = useState<string>()
  const [originalWidth, setOriginalWidth] = useState<number>(0)
  const [originalHeight, setOriginalHeight] = useState<number>(0)
  const originalAspectRatio = originalWidth / (originalHeight || 1)

  const channel = wizardSchema.meta.channel as DesignChannel
  const { width, height, initial_media_file: selectedImage, startWithTemplate } = initialValues

  const initialAspectRatio = getInitialAspectRatio(width, height)
  const [type, setType] = useState<AspectRatioType>(initialAspectRatio)
  const aspectRatios = wizardSchema.meta.aspectRatios || {}
  const aspectRatioTypes = Object.keys(aspectRatios) as AspectRatioType[]
  const showAspectRatioControl = (aspectRatioTypes?.length ?? 0) > 1 && !isLoading && !startWithTemplate
  const aspectRatioKey = aspectRatios[type] || getAspectRatioKeyFromDesignChannel(channel, 0, 0)
  const { width: selectedWidth, height: selectedHeight } = ASPECT_RATIOS[aspectRatioKey]
  const dynamicAspectRatio = useMemo(() => selectedWidth / selectedHeight, [selectedHeight, selectedWidth])

  const aspectRatioSelectorHeight = 52 // Height of the aspect ratio SegmentedControl selector
  const wizardElementsHeight = 115 // Height of the title, remove-background toggle and their gaps added up
  const imageMaxHeight = showAspectRatioControl
    ? `calc(100% - ${wizardElementsHeight + aspectRatioSelectorHeight}px)`
    : `calc(100% - ${wizardElementsHeight}px)`
  const imageAspectRatio = showAspectRatioControl ? dynamicAspectRatio : originalAspectRatio

  const backgroundVariant = selectedImage.variants?.find((v: MediaVariant) => !!v.transformations.remove_background)
  const originalUrl = selectedImage.source_url || selectedImage.url
  const removedBackgroundUrl = backgroundVariant?.source_url || cutoutUrl
  const src = useMemo(
    () => (isBackgroundRemoved && removedBackgroundUrl ? removedBackgroundUrl : originalUrl),
    [isBackgroundRemoved, originalUrl, removedBackgroundUrl],
  )
  const handleBack = () => {
    setCutoutUrl(undefined)
    onBack?.()
  }

  // Abort background removal on component dismount
  useEffect(
    () => () => {
      abortController.current.abort('cancel')
      abortController.current = new AbortController()
    },
    [],
  )

  const removeBackground = () => {
    if (!!removedBackgroundUrl || !selectedImage.id) return

    mutate(
      {
        mediaFileId: selectedImage.id,
        timeout: 20000, // 20 seconds
        signal: abortController.current.signal,
        transformations: {
          remove_background: { crop: true },
        },
      },
      {
        onError: () => {
          setIsBackgroundRemoved(false)
          showToastMessage("We weren't able to remove the background of this image.", 'error')
          handleBack()
        },
        onSuccess: (data) => {
          setCutoutUrl(data.source_url)
        },
      },
    )
  }

  const updateType = (newType: AspectRatioType) => {
    const aspectRatioKey = aspectRatios[newType] || getAspectRatioKeyFromDesignChannel(channel, 0, 0)
    const { width, height } = ASPECT_RATIOS[aspectRatioKey]

    setType(newType)
    setFieldValue('width', width)
    setFieldValue('height', height)
  }

  const onImageLoad = (e: SyntheticEvent<HTMLImageElement, Event>) => {
    if (!originalAspectRatio) {
      setOriginalWidth(e.currentTarget.width)
      setOriginalHeight(e.currentTarget.height)
    }
  }

  useEffect(() => {
    if (initialRemoveBackground) {
      removeBackground()
      setFieldValue('initial_media_file_remove_background', true)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Form.Root>
      <Form.Section>
        <div
          className={cx(Styles.container, {
            [Styles.portrait]: originalAspectRatio < 1 || dynamicAspectRatio < 1,
            [Styles.dynamicAspectRatio]: dynamicAspectRatio,
          })}
        >
          <div className={Styles.header}>
            <Heading level={2}>{schema.describe().meta.title}</Heading>
            {showAspectRatioControl && (
              <div className={Styles.toggle}>
                <SegmentedControl
                  onItemClick={(name) => updateType(name as AspectRatioType)}
                  activeItem={type}
                  className={Styles.toggleElement}
                  variant="secondary"
                  rounded
                  size="sm"
                  items={aspectRatioTypes.map((type) => ({
                    id: type,
                    name: capitalize(type),
                  }))}
                >
                  {(item) => <Item>{item.name}</Item>}
                </SegmentedControl>
              </div>
            )}
          </div>
          {isLoading ? (
            <>
              <LoadingShimmerer className={Styles.shimmererContainer}>
                <img
                  alt="selected"
                  src={originalUrl}
                  ref={originalImageRef}
                  className={cx(Styles.selectedImage, Styles.isLoading)}
                  onLoad={onImageLoad}
                  style={{ aspectRatio: imageAspectRatio }}
                />
              </LoadingShimmerer>
              <div className={Styles.loadingLabel}>
                <Text variant="largeList" color="var(--dark-40)">
                  Removing background
                  <LoadingEllipses />
                </Text>
              </div>
            </>
          ) : (
            <>
              <img
                alt="selected"
                src={src}
                className={cx(Styles.selectedImage, {
                  [Styles.backgroundRemoved]: isBackgroundRemoved || showAspectRatioControl,
                })}
                style={{
                  aspectRatio: imageAspectRatio,
                  width: showAspectRatioControl ? undefined : originalWidth,
                  height: showAspectRatioControl ? undefined : originalHeight,
                  maxHeight: imageMaxHeight,
                }}
              />
              <img
                alt="dummy img to extract aspect ratio of original url"
                src={originalUrl}
                className={cx(Styles.selectedImage, Styles.dummyImg)}
                onLoad={onImageLoad}
                style={{
                  aspectRatio: imageAspectRatio,
                  maxHeight: imageMaxHeight,
                }}
              />
              <div className={cx(Styles.removeBackground)}>
                <div className={cx(Styles.tag)}>
                  <Background />
                  <Text variant="largeList">Remove Background</Text>
                </div>
                <ToggleButton
                  name="Remove Background"
                  checked={isBackgroundRemoved}
                  handleChange={(e) => {
                    removeBackground()
                    setFieldValue('initial_media_file_remove_background', !isBackgroundRemoved)
                    setIsBackgroundRemoved((value) => !value)
                  }}
                  disabled={isLoading}
                />
              </div>
            </>
          )}
        </div>
      </Form.Section>
      <Form.Actions isSticky>
        <Form.PreviousButton onPress={() => handleBack()}>
          {schema.describe().meta.previousLabel || 'Image Options'}
        </Form.PreviousButton>
        <Form.PrimaryActions>
          <Form.SubmitButton isDisabled={isLoading} variant="primary">
            {schema.describe().meta.submitLabel}
          </Form.SubmitButton>
        </Form.PrimaryActions>
      </Form.Actions>
    </Form.Root>
  )
})
